import React, { useState } from "react";
import axios from "axios";
import { closePopup } from "../../utils/popupUtilities";
import { InputProps } from "../form_components/Input";
import MultiSelect, { MultiSelectProps } from "../form_components/MultiSelect";
import Button from "../Button";

type FormValues = Record<string, string | boolean>;

type FormBuilderRenderProps = {
  values: FormValues;
  setFieldValue: (name: string, value: string | boolean) => void;
};

type FormBuilderProps = {
  title: string;
  post?: string;
  children: React.ReactNode | ((helpers: FormBuilderRenderProps) => React.ReactNode);
  onSubmit?: () => void;
  initialValues?: FormValues;
};

export default function FormBuilder({
  title,
  post,
  children,
  onSubmit,
  initialValues = {},
}: FormBuilderProps) {
  const [formValues, setFormValues] = useState<FormValues>(initialValues);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const formRef = React.useRef<HTMLFormElement>(null);

  const updateFormValues = (field: string, value: string | boolean) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      [field]: value,
    }));
  };

  const onClick = async (e: React.MouseEvent) => {
    e.preventDefault();

    const form = formRef.current;
    if (form && !form.checkValidity()) {
      form.reportValidity();
      return;
    }

    setErrorMessage("");

    if (post) {
      try {
        await axios.post((process.env.REACT_APP_API_URI ?? "") + post, formValues);
        if (onSubmit) onSubmit();
        closePopup();
      } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
          console.error(error.response.data);
          setErrorMessage(error.response.data.message || "A server side error has occurred.");
        } else {
          console.error(error);
          setErrorMessage(error instanceof Error ? error.message : "A server side error has occurred.");
        }
      }
    } else {
      if (onSubmit) {
        onSubmit();
        console.log(formValues);
        closePopup();
      }
    }
  };

const enhanceChildren = (nodes: React.ReactNode): React.ReactNode =>
  React.Children.map(nodes, (child) => {
    if (!React.isValidElement(child)) return child;

    const props = child.props as any;

    // Inject only into your "field" components
    if (typeof props?.name === "string") {
      const originalOnChange = props.onChange as
        | undefined
        | ((name: string, value: string | boolean) => void);

      const composedOnChange = (name: string, value: string | boolean) => {
        updateFormValues(name, value);
        if (originalOnChange) originalOnChange(name, value);
      };

      const nextValue =
        formValues[props.name] ?? (props.type === "checkbox" ? false : "");

      return React.cloneElement(child as React.ReactElement<any>, {
        value: nextValue,
        onChange: composedOnChange,
      });
    }

    // Recurse into wrappers (div, fragment, etc.)
    if (props?.children) {
      return React.cloneElement(child as React.ReactElement<any>, {
        children: enhanceChildren(props.children),
      });
    }

    return child;
  });

  const renderedChildren =
    typeof children === "function"
      ? children({ values: formValues, setFieldValue: updateFormValues })
      : children;

  const childrenWithProps = enhanceChildren(renderedChildren);

  return (
    <div className="relative">
      <h3 className="w-full text-center text-5xl mb-3">{title}</h3>
      <div className="error-message w-full text-center text-red-600 mb-2">
        {errorMessage}
      </div>

      <form ref={formRef}>
        {childrenWithProps}
      </form>

      <Button submit={true} extraClasses="mt-4" label="Apply" width="full" onClick={onClick} />
      {process.env.REACT_APP_DEBUG == "true" ?
        <div className="p-4 bg-gray-100 rounded shadow">
          <pre>{JSON.stringify(formValues, null, 2)}</pre>
        </div>
        : <div></div>}
    </div>
  );
}